import {
	deleteInterventionOptions,
	detachInterventionFromGoalOptions,
	InterventionDefinitionTypeEnum,
	type DeleteInterventionCommand,
	type DetachInterventionFromGoalCommand,
	type GoalViewModel,
	type InterventionViewModel
} from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMutation } from '~/util/http/useMutation';
import { Prompt } from '~/shared/components/Prompt';
import { SectionListRowButtons } from '~/shared/section/section-list/SectionListRowButtons';
import { DisciplineChip } from '~/libraries/disciplines/DisciplineChip';
import { Button } from '~/shared/components/buttons/Button';
import { ListX } from 'lucide-react';
import { useGoalsAndnterventionsForOrder } from '../queries';
import { useFullOrder } from '~/features/orders/orders/FullOrderContext';
import { EditPredefinedInterventionFormModal } from './EditPredefinedInterventionFormModal';
import { EditCustomInterventionFormModal } from './EditCustomInterventionFormModal';

export function InterventionOrderRow({ goal, intervention }: { goal: GoalViewModel; intervention: InterventionViewModel }) {
	const { fullOrder } = useFullOrder();
	const { refetch } = useSuspenseQuery(useGoalsAndnterventionsForOrder(fullOrder.id));
	const deleteIntervention = useMutation<void, DeleteInterventionCommand>(deleteInterventionOptions);
	const detachIntervention = useMutation<void, DetachInterventionFromGoalCommand>(detachInterventionFromGoalOptions);

	const onDetachIntervention = (goalId: string, interventionId: string) => {
		detachIntervention.mutate(
			{ goalId, interventionId },
			{
				onSuccess: () => {
					refetch();
				}
			}
		);
	};

	return (
		<li
			key={intervention.id}
			className="group/iv flex items-center justify-between gap-3 rounded-md border border-transparent pr-2 transition-colors"
		>
			<span className="size-1.5 rounded-full bg-gray-400" aria-hidden />
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<span className="truncate text-sm text-gray-700">{intervention.definitionText}</span>
				<div className="flex items-center gap-1.5">
					{intervention.assignedDisciplines.map(discipline => (
						<DisciplineChip
							key={discipline.assignedDisciplineId}
							disciplineId={discipline.assignedDisciplineId}
							useAbbreviation
							className="py-0.5"
						/>
					))}
				</div>
			</div>
			<SectionListRowButtons
				editButtonProps={
					(intervention.interventionDefinition && intervention.interventionDefinition.hasDefinitionFields) ||
					intervention.interventionDefinitionTypeId === InterventionDefinitionTypeEnum.Custom
						? {
								label: 'Edit Intervention',
								renderModal:
									intervention.interventionDefinitionTypeId === InterventionDefinitionTypeEnum.Predefined
										? closeModal => (
												<EditPredefinedInterventionFormModal
													interventionId={intervention.id}
													fullOrder={fullOrder}
													closeModal={closeModal}
												/>
										  )
										: closeModal => (
												<EditCustomInterventionFormModal
													interventionId={intervention.id}
													fullOrder={fullOrder}
													closeModal={closeModal}
												/>
										  )
						  }
						: undefined
				}
				removeButtonProps={{
					label: 'Remove Intervention',
					isPending: deleteIntervention.isPending && deleteIntervention.variables?.interventionId === intervention.id,
					renderModal: closeModal => (
						<Prompt
							title="Remove Intervention"
							text="Are you sure you want to remove this interventions?"
							onAction={() => {
								deleteIntervention.mutate(
									{ orderId: fullOrder.id, interventionId: intervention.id },
									{
										onSuccess: () => {
											refetch();
										}
									}
								);
								closeModal();
							}}
							onCancel={closeModal}
						/>
					)
				}}
				buttonSlot={
					goal.invariants.canBeAttachedTo && (
						<Button
							aria-label="Detach Intervention"
							size="xs"
							appearance="ghost"
							intent="neutralLight"
							Icon={ListX}
							isPending={detachIntervention.isPending && detachIntervention.variables?.interventionId === intervention.id}
							useTooltip
							renderModal={closeModal => (
								<Prompt
									title="Detach Intervention"
									text="Are you sure you want to detach this intervention?"
									onAction={() => {
										onDetachIntervention(goal.id, intervention.id);
										closeModal();
									}}
									onCancel={closeModal}
								/>
							)}
						/>
					)
				}
			/>
		</li>
	);
}
